<template>
  <div ref="dateFilterComponent" class="h-full flex flex-wrap items-center gap-2 relative">
    <button
      v-if="dateFiltered.length > 0"
      class="min-w-[60px] bg-red-500 dark:bg-[#FF4980] dark:hover:bg-[#FF6694] px-4 py-2  button-filter"
      @click="clear"
    >
      <pe-icon icon="delete" /> {{ t('button.clear') }}
    </button>
    <date-picker
      v-model="dateFiltered"
      auto-apply
      range
      :locale="getLocale"
      format="dd/MM/yyyy"
      :enableTimePicker="false"
      :dark="systemStore.isDark"
      @open="open = true"
      @closed="open = false"
      @update:model-value="onChangeDate"
      class="w-14"
    >
      <template #trigger>
        <button
          class="min-w-0 p-2 bg-gray-500 dark:bg-neutral-800 button-filter"
        >
          <pe-icon icon="date_range" :size="18" />
          <pe-icon :icon="open ? 'arrow_drop_up' : 'arrow_drop_down'" :size="18" />
        </button>
      </template>
    </date-picker>
    <button
      v-for="(item, index) in [
        [0, t('button.today')],
        [1, t('button.yesterday')],
        [2, t('button.thisWeek')],
        [3, t('button.thisMonth')]
      ]"
      :key="index"
      class="min-w-[60px] px-4 py-2 button-filter"
      :class="{ 
        'pointer-events-none bg-[#5D5FEF] dark:bg-[#5D5FEF]/70': selected === index, 
        'bg-gray-500 dark:bg-neutral-800': selected !== index, 
      }"
      @click="setDate(index)"
    >
      {{ item[1] }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ref, onMounted } from 'vue'
import {useI18n} from 'vue-i18n'
import { useSystemStore } from '@/stores'

const props = defineProps<{
  modelValue?: any,
  defaultTime?: 'month' | 'week' | 'today' | 'yesterday'
}>()

const systemStore = useSystemStore()

const emit = defineEmits(['update:modelValue'])

const {t, locale} = useI18n()

const dateFiltered = ref([])
const selected = ref(-1)
const open = ref(false)
// const dateFilterComponent = ref()

const getLocale = computed(() => ({
  'en': 'en-US',
  'pt': 'pt-BR',
  'es': 'es-ES'
}[locale.value]))

const setDate = (index: number): void => {
  selected.value = index
  const now = new Date()

  if (index === 3) { // Month
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    dateFiltered.value = [firstDay, lastDay]
  }
  if (index === 2) { // week
    const firstDay = new Date(now.setDate(now.getDate() - now.getDay()))
    const lastDay = new Date(now.setDate(now.getDate() - now.getDay() + 6))
    dateFiltered.value = [firstDay, lastDay]
  }  
  if (index === 1) // yesterday
    dateFiltered.value = [getYesterday(), getYesterday(true)]
  if (index === 0) // today
    dateFiltered.value = [now, now]

  emit('update:modelValue', dateFiltered.value)
}

const getYesterday = (end?: boolean) => {
  const today = new Date()
  const yesterday = new Date(today)

  yesterday.setDate(today.getDate() - 1)
  end ? yesterday.setUTCHours(23, 59, 59) : yesterday.setUTCHours(0, 0, 0)
  return yesterday.getTime()
}

const getDateTime = (date: Date[]) => {
  const first = new Date(date[0].setUTCHours(0, 0, 0, 0)).getTime()
  const last = new Date(date[1].setUTCHours(23, 59, 59, 59)).getTime()

  return [first, last]
}

const onChangeDate = (date: Date[]) => {
  dateFiltered.value = getDateTime(date)
  selected.value = -1
  emit('update:modelValue', dateFiltered.value)
}

const clear = () => {
  selected.value = -1
  dateFiltered.value = []
  emit('update:modelValue', [])
}

onMounted(() => {
  if (props.defaultTime && ['today', 'yesterday', 'week', 'month'].includes(props.defaultTime))
    setDate(['today', 'yesterday', 'week', 'month'].indexOf(props.defaultTime))
})
</script>

<style lang="scss" scoped>
.button-filter {
  @apply whitespace-nowrap rounded-lg border-0 text-center text-sm text-white drop-shadow-[0_0_2px_rgba(0,0,0,.1)] transition-colors hover:cursor-pointer;
}
</style>
